import { useCallback, useRef } from 'react'

import { joinDictation } from './format-dictation'
import { useVoiceDictation } from './use-voice-dictation'

/**
 * Wire voice dictation onto a plain-text draft (textarea / controlled string).
 * The user records, then on stop the transcript is appended once — with smart
 * punctuation and spacing — onto whatever the field held when they tapped the
 * mic. Returns the recording/processing state, the live mic stream (for the
 * waveform), and start/stop controls. The owning component decides where to
 * render the mic and island.
 */
export function useVoiceDraft(value: string, onChange: (next: string) => void) {
  const valueRef = useRef(value)
  valueRef.current = value
  const baseTextRef = useRef('')
  const handleTranscript = useCallback(
    (sessionText: string) =>
      onChange(joinDictation(baseTextRef.current, sessionText)),
    [onChange],
  )
  const { recording, loading, processing, stream, start, stop } =
    useVoiceDictation({
      onTranscript: handleTranscript,
    })
  const startRecording = useCallback(() => {
    baseTextRef.current = valueRef.current
    void start()
  }, [start])
  return {
    recording,
    loading,
    processing,
    voiceStream: stream,
    startRecording,
    stopRecording: stop,
  }
}
